/**
 * ORDER-2 — the ONE derivation of a protective limit price.
 *
 * The live page had three tick-rounding implementations and the basket had
 * none, so basket orders were posted at prices like 1.68 and rejected by the
 * broker: `ORA:Price 1.68 is not a multiple of tick size 0.05`. Found live
 * 2026-08-13 during SENSEX expiry, an hour after ORDER-1 — the same root cause
 * wearing different clothes: the basket is a second implementation of order
 * submission, missing the pieces the first one accumulated.
 *
 * Every surface that turns an LTP into a limit price now goes through here:
 * submitOrder (single, hedged, chase/unwind) and the basket loop. Adding a
 * fourth caller means editing this file, which is the point.
 *
 * ── ROUNDING DIRECTION: NEAREST, deliberately ────────────────────────────────
 *
 * This is a real-money behaviour choice, not a rounding detail, so it is stated
 * rather than implied.
 *
 * `Math.round` moves the price to the NEAREST tick, which for a BUY can round
 * DOWN by up to half a tick — very slightly tightening a protection band the
 * operator set, and making a fill marginally less likely. The alternative is to
 * round AWAY from the mid (BUY up, SELL down), which always preserves the band's
 * intent and pays at most half a tick more.
 *
 * NEAREST is kept because it is what `submitOrder` has done since ff059b7 and is
 * therefore what every live single order has already been priced at. Changing the
 * direction would silently move real limit prices on the path that works today,
 * which is a bigger change than the defect being fixed and belongs in its own
 * brief. The basket matches it so the two paths agree — that invariant is what
 * stops them diverging again.
 *
 * If that trade is ever revisited, change `tickAlign` here and BOTH paths move
 * together. The direction is asserted explicitly in the tests, so a change fails
 * a test rather than quietly repricing orders.
 */

/**
 * Fallback tick when the broker gives none.
 *
 * The tick belongs to the CONTRACT — `search_scrip` returns `ti` per instrument
 * and it differs by segment and can change. 0.05 is only the fallback, matching
 * what live.html already used at its resolve sites, so both order paths behave
 * identically when `ti` is missing. It is never the rule.
 */
export const DEFAULT_TICK = 0.05;

/** The contract's tick if it is usable, else DEFAULT_TICK. Never 0 or NaN. */
export function tickOf(ti: unknown): number {
  const tick = typeof ti === "number" ? ti : parseFloat(String(ti));
  return isFinite(tick) && tick > 0 ? tick : DEFAULT_TICK;
}

/**
 * Snap a price to the contract's tick grid — nearest tick (see the note above).
 *
 * A non-positive or unparseable tick falls back to DEFAULT_TICK rather than
 * dividing by zero: `price / 0` is Infinity and would post a NaN price.
 */
export function tickAlign(price: number, ti: unknown): number {
  const tick = tickOf(ti);
  if (!isFinite(price)) return NaN;
  return +(Math.round(price / tick) * tick).toFixed(2);
}

/**
 * The marketable-limit price for one leg: LTP crossed by `band`, tick-aligned.
 *
 * BUY pays up (ltp * (1 + band)), SELL sells down (ltp * (1 - band)) — the
 * protective band that makes the limit marketable. Returns NaN when the inputs
 * cannot produce a price, so a caller must refuse rather than post a bad one.
 */
export function protectiveLimitPrice(
  ltp: number | null | undefined, side: string, band: number, ti: unknown,
): number {
  const px = Number(ltp);
  if (!isFinite(px) || px <= 0) return NaN;
  const b = isFinite(Number(band)) ? Number(band) : 0;
  const raw = String(side).toUpperCase() === "BUY" ? px * (1 + b) : px * (1 - b);
  return tickAlign(raw, ti);
}
